import { useCallback, useRef } from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import styles from './CasFormConditionalInput.module.scss';
import CasFormInput from '../cas-form-input/CasFormInput';
import { VdsCheckbox } from '@volt/volt-extended-components-react';
import type { CheckboxEvent, VdsCheckboxCustomEvent } from '@volt/volt-basic-components/dist/types';

export interface CasFormConditionalInputProps<TFormValues extends FieldValues>
  extends UseControllerProps<TFormValues> {
  label: string;
  isReadonly?: boolean;
}

export default function CasFormConditionalInput<TFormValues extends FieldValues>(
  props: CasFormConditionalInputProps<TFormValues>,
) {
  const { disabled, isReadonly, label, ...sanitisedProps } = props;
  const { field, formState } = useController(sanitisedProps);

  // Keep a record of the input text value to restore it if/when the checkbox is toggled.
  // Use a ref to avoid triggering re-renders when the record is updated.
  const valueRef = useRef<string>(field.value ?? '');

  const onCheckboxChange = useCallback(
    (e: VdsCheckboxCustomEvent<CheckboxEvent>) => {
      const isChecked = e.target.checked;

      if (!isChecked) {
        // First, save any text value to the ref.
        valueRef.current = field.value ?? '';
        // Then remove the text value from the RHF state.
        field.onChange(null);
      } else {
        // If the field has been enabled, load any saved text value from the ref.
        field.onChange(valueRef.current);
      }
    },
    [field],
  );

  const hasValue = field.value !== null;

  const isCheckboxDisabled = props.disabled || field.disabled || formState.isSubmitting;

  return (
    <div className={styles.conditionalWrapper}>
      <VdsCheckbox
        labelValue={label}
        size="medium"
        checked={hasValue}
        onCheckboxChange={onCheckboxChange}
        disabled={isCheckboxDisabled || isReadonly}
      />

      {/* Errors are displayed and handled in the input field. */}
      <CasFormInput
        control={props.control}
        name={props.name}
        label=""
        element="input"
        type="text"
        disabled={!hasValue}
        isReadonly={isReadonly}
      />
    </div>
  );
}
